import { Component, Input, OnInit } from '@angular/core';
import { Block, BlockAbstractComponent } from '@gallaghercp/core-frontend';
import { BlockTimelineChartComponent } from '@core-app/blocks/block-leave-of-absence-tool/results/timeline-chart/block-timeline-chart.component';
import { TranslatePipe } from '@gallaghercp/core-frontend-contracts';

@Component({
    selector: 'core-surgery-results',
    templateUrl: './block-surgery-results.component.html',
    styleUrls: [
        '../../scss/block-leave-of-absence-tool-charts.scss',
        '../../scss/block-leave-of-absence-tool-questions.scss',
        '../../scss/block-leave-of-absence-tool-timeline.scss',
    ],
    imports: [BlockTimelineChartComponent, TranslatePipe],
})
export class BlockSurgeryResultsComponent extends BlockAbstractComponent implements OnInit {
    @Input() selectedAnswers: Record<string, any> = {};
    @Input() surgeryTimelineChartKey: string = '';
    @Input() parentBlock: Block | undefined;

    // Section text strings
    introParagraphText!: string;
    lengthOfLeaveText!: string;
    payText!: string;
    processChecklist!: string;
    thingsToConsider!: string;

    override ngOnInit() {
        // Create a mapping of conditions to their corresponding intro paragraph text
        const introParagraphMapping = [
            {
                condition: this.surgeryTwelveMonthsOrMoreHourlyShort,
                text: this.parentBlock?.data.surgeryIntroParagraphLeaveQualified,
            },
            {
                condition: this.surgeryTwelveMonthsOrMoreHourlyLong,
                text: this.parentBlock?.data.surgeryIntroParagraphLeaveQualified,
            },
            {
                condition: this.surgeryTwelveMonthsOrMoreSalariedShort,
                text: this.parentBlock?.data.surgeryIntroParagraphLeaveThreeCalendarDaysStdAtOneHundredPercent,
            },
            {
                condition: this.surgeryTwelveMonthsOrMoreSalariedLong,
                text: this.parentBlock?.data.surgeryIntroParagraphLeaveQualified,
            },
            {
                condition: this.surgeryLessThanTwelveMonthsButMoreThanNinetyDaysHourlyShort,
                text: this.parentBlock?.data.surgeryIntroParagraphLeaveNotQualifiedStdAtSixtyPercent,
            },
            {
                condition: this.surgeryLessThanTwelveMonthsButMoreThanNinetyDaysHourlyLong,
                text: this.parentBlock?.data.surgeryIntroParagraphLeaveQualified,
            },
            {
                condition: this.surgeryLessThanTwelveMonthsButMoreThanNinetyDaysSalariedShort,
                text: this.parentBlock?.data.surgeryIntroParagraphLeaveNotQualifiedStdAtOneHundredPercent,
            },
            {
                condition: this.surgeryLessThanTwelveMonthsButMoreThanNinetyDaysSalariedLong,
                text: this.parentBlock?.data.surgeryIntroParagraphLeaveQualified,
            },
            {
                condition: this.surgeryNinetyDaysOrLess,
                text: this.parentBlock?.data.surgeryIntroParagraphLeaveNotQualifiedContactHr,
            },
        ];

        // Create a mapping of conditions to their corresponding length of leave text
        const lengthOfLeaveMapping = [
            {
                condition: this.surgeryTwelveMonthsOrMoreHourlyShort,
                text: this.parentBlock?.data.surgeryLengthOfLeaveMoreThanThreeCalendarDaysNotQualified,
            },
            {
                condition: this.surgeryTwelveMonthsOrMoreHourlyLong,
                text: this.parentBlock?.data.surgeryLengthOfLeaveUpToTwelveMonths,
            },
            {
                condition: this.surgeryTwelveMonthsOrMoreSalariedLong,
                text: this.parentBlock?.data.surgeryLengthOfLeaveUpToTwelveMonths,
            },
            {
                condition: this.surgeryLessThanTwelveMonthsButMoreThanNinetyDaysHourlyLong,
                text: this.parentBlock?.data.surgeryLengthOfLeaveUpToSixMonths,
            },
            {
                condition: this.surgeryLessThanTwelveMonthsButMoreThanNinetyDaysSalariedLong,
                text: this.parentBlock?.data.surgeryLengthOfLeaveUpToSixMonths,
            },
        ];

        // Create a mapping of conditions to their corresponding pay text
        const payMapping = [
            {
                condition: this.surgeryTwelveMonthsOrMoreHourlyShort,
                text: this.parentBlock?.data.surgeryPaySevenDaysWaitingPeriodStdAtSixtyPercent,
            },
            {
                condition: this.surgeryTwelveMonthsOrMoreHourlyLong,
                text: this.parentBlock?.data
                    .surgeryPaySevenDaysWaitingPeriodStdAtSixtyPercentOverTwentyWeeksLtdAtFiftyPercent,
            },
            {
                condition: this.surgeryTwelveMonthsOrMoreSalariedLong,
                text: this.parentBlock?.data.surgeryPayStdAtOneHundredPercentOverTwentyWeeksLtdAtFiftyPercent,
            },
            {
                condition: this.surgeryLessThanTwelveMonthsButMoreThanNinetyDaysHourlyLong,
                text: this.parentBlock?.data.surgeryPaySevenDaysWaitingPeriodStdAtSixtyPercentOverTwentyWeeks,
            },
            {
                condition: this.surgeryLessThanTwelveMonthsButMoreThanNinetyDaysSalariedLong,
                text: this.parentBlock?.data.surgeryPayStdAtOneHundredPercent,
            },
        ];

        // Create a mapping of conditions to their corresponding process list text
        const processListMapping = [
            {
                condition: this.surgeryTwelveMonthsOrMoreHourlyShort,
                text: this.parentBlock?.data.surgeryProcessListOne,
            },
            {
                condition: this.surgeryTwelveMonthsOrMoreHourlyLong,
                text: this.parentBlock?.data.surgeryProcessListTwo,
            },
            {
                condition: this.surgeryTwelveMonthsOrMoreSalariedLong,
                text: this.parentBlock?.data.surgeryProcessListTwo,
            },
            {
                condition: this.surgeryLessThanTwelveMonthsButMoreThanNinetyDaysHourlyLong,
                text: this.parentBlock?.data.surgeryProcessListTwo,
            },
            {
                condition: this.surgeryLessThanTwelveMonthsButMoreThanNinetyDaysSalariedLong,
                text: this.parentBlock?.data.surgeryProcessListTwo,
            },
        ];

        // Create a mapping of conditions to their corresponding things to consider text
        const thingsToConsiderMapping = [
            {
                condition: this.surgeryTwelveMonthsOrMoreHourlyShort,
                text: this.parentBlock?.data.surgeryThingsToConsiderOne,
            },
            {
                condition: this.surgeryTwelveMonthsOrMoreHourlyLong,
                text: this.parentBlock?.data.surgeryThingsToConsiderOne,
            },
            {
                condition: this.surgeryTwelveMonthsOrMoreSalariedLong,
                text: this.parentBlock?.data.surgeryThingsToConsiderOne,
            },
            {
                condition: this.surgeryLessThanTwelveMonthsButMoreThanNinetyDaysHourlyLong,
                text: this.parentBlock?.data.surgeryThingsToConsiderOne,
            },
            {
                condition: this.surgeryLessThanTwelveMonthsButMoreThanNinetyDaysSalariedLong,
                text: this.parentBlock?.data.surgeryThingsToConsiderOne,
            },
            {
                condition: this.surgeryNinetyDaysOrLess,
                text: this.parentBlock?.data.surgeryThingsToConsiderTwo,
            },
        ];

        // Find the first matching condition and set the intro paragraph text
        const introParagraphMatch = introParagraphMapping.find(item => item.condition);
        if (introParagraphMatch) {
            this.introParagraphText = introParagraphMatch.text;
        }

        // Find the first matching condition and set the length of leave text
        const lengthOfLeaveMatch = lengthOfLeaveMapping.find(item => item.condition);
        if (lengthOfLeaveMatch) {
            this.lengthOfLeaveText = lengthOfLeaveMatch.text;
        }

        // Find the first matching condition and set the pay text
        const payMatch = payMapping.find(item => item.condition);
        if (payMatch) {
            this.payText = payMatch.text;
        }

        // Find the first matching condition and set the process list text
        const processListMatch = processListMapping.find(item => item.condition);
        if (processListMatch) {
            this.processChecklist = processListMatch.text;
        }

        // Find the first matching condition and set the things to consider text
        const thingsToConsiderMatch = thingsToConsiderMapping.find(item => item.condition);
        if (thingsToConsiderMatch) {
            this.thingsToConsider = thingsToConsiderMatch.text;
        }
        super.ngOnInit();
    }

    get surgeryTwelveMonthsOrMoreHourlyShort(): boolean {
        return (
            this.selectedAnswers['employedDuration'] == 'twelveMonthsOrMore' &&
            this.selectedAnswers['hourlyOrSalaried'] == 'hourly' &&
            this.selectedAnswers['timeAwayFromWork'] == 'short'
        );
    }

    get surgeryTwelveMonthsOrMoreHourlyLong(): boolean {
        return (
            this.selectedAnswers['employedDuration'] == 'twelveMonthsOrMore' &&
            this.selectedAnswers['hourlyOrSalaried'] == 'hourly' &&
            this.selectedAnswers['timeAwayFromWork'] == 'long'
        );
    }

    get surgeryTwelveMonthsOrMoreSalariedShort(): boolean {
        return (
            this.selectedAnswers['employedDuration'] == 'twelveMonthsOrMore' &&
            this.selectedAnswers['hourlyOrSalaried'] == 'salaried' &&
            this.selectedAnswers['timeAwayFromWork'] == 'short'
        );
    }

    get surgeryTwelveMonthsOrMoreSalariedLong(): boolean {
        return (
            this.selectedAnswers['employedDuration'] == 'twelveMonthsOrMore' &&
            this.selectedAnswers['hourlyOrSalaried'] == 'salaried' &&
            this.selectedAnswers['timeAwayFromWork'] == 'long'
        );
    }

    get surgeryLessThanTwelveMonthsButMoreThanNinetyDaysHourlyShort(): boolean {
        return (
            this.selectedAnswers['employedDuration'] == 'lessThanTwelveMonthsButMoreThanNinetyDays' &&
            this.selectedAnswers['hourlyOrSalaried'] == 'hourly' &&
            this.selectedAnswers['timeAwayFromWork'] == 'short'
        );
    }

    get surgeryLessThanTwelveMonthsButMoreThanNinetyDaysHourlyLong(): boolean {
        return (
            this.selectedAnswers['employedDuration'] == 'lessThanTwelveMonthsButMoreThanNinetyDays' &&
            this.selectedAnswers['hourlyOrSalaried'] == 'hourly' &&
            this.selectedAnswers['timeAwayFromWork'] == 'long'
        );
    }

    get surgeryLessThanTwelveMonthsButMoreThanNinetyDaysSalariedShort(): boolean {
        return (
            this.selectedAnswers['employedDuration'] == 'lessThanTwelveMonthsButMoreThanNinetyDays' &&
            this.selectedAnswers['hourlyOrSalaried'] == 'salaried' &&
            this.selectedAnswers['timeAwayFromWork'] == 'short'
        );
    }

    get surgeryLessThanTwelveMonthsButMoreThanNinetyDaysSalariedLong(): boolean {
        return (
            this.selectedAnswers['employedDuration'] == 'lessThanTwelveMonthsButMoreThanNinetyDays' &&
            this.selectedAnswers['hourlyOrSalaried'] == 'salaried' &&
            this.selectedAnswers['timeAwayFromWork'] == 'long'
        );
    }

    get surgeryNinetyDaysOrLess(): boolean {
        return this.selectedAnswers['employedDuration'] == 'ninetyDaysOrLess';
    }
}
